import { ANIMATION_DURATIONS } from '../../../constants/animations';

/**
 * Calcula o tempo restante para completar o tempo mínimo de exibição
 *
 * Útil para garantir que um elemento seja exibido por tempo mínimo, mesmo que
 * o processo termine antes. Por exemplo, garantir que o loading apareça por pelo
 * menos 2 segundos.
 *
 * @param {number} startTime - Timestamp em ms de quando iniciou (Date.now())
 * @param {number} minDuration - Duração mínima desejada em ms
 * @returns {number} Tempo restante em ms (sempre >= 0)
 *
 * @example
 * const startTime = Date.now();
 * // ... após algum tempo ...
 * const remaining = calculateRemainingTime(startTime, 2000); // quanto falta para 2s
 */
export const calculateRemainingTime = (startTime, minDuration) => {
  const elapsedTime = Date.now() - startTime;
  return Math.max(0, minDuration - elapsedTime);
};

/**
 * Cria uma sequência encadeada de timeouts para transição de status
 *
 * Gerencia a sequência completa de animações ao sair de um status (loading/success/error):
 * 1. Aguarda tempo mínimo de exibição (remainingTime)
 * 2. Inicia fadeOut do GlobalProgress (ANIMATION_DURATIONS.BASE)
 * 3. Troca para conteúdo e inicia animação de altura (ANIMATION_DURATIONS.BASE)
 * 4. Aplica delay e fadeIn no conteúdo (ANIMATION_DURATIONS.BASE * 2)
 *
 * @param {Object} callbacks - Callbacks para cada etapa da transição
 * @param {Function} [callbacks.onFadeOutStart] - Chamado ao iniciar fadeOut do status
 * @param {Function} [callbacks.onStatusHidden] - Chamado após status desaparecer
 * @param {Function} [callbacks.onContentDelayEnd] - Chamado ao fim do delay do conteúdo
 * @param {Function} [callbacks.onTransitionComplete] - Chamado ao fim de toda transição
 * @param {number} remainingTime - Tempo inicial de espera em ms antes de iniciar transição
 * @returns {Function} Cleanup function que cancela todos os timeouts pendentes
 *
 * @example
 * const cleanup = createStatusTransitionSequence({
 *   onFadeOutStart: () => setStatusForProgress(null),
 *   onStatusHidden: () => setDisplayStatus(null),
 *   onContentDelayEnd: () => setContentDelay(false),
 *   onTransitionComplete: () => setFromLoading(false)
 * }, 1500);
 * // Para cancelar: cleanup();
 */
export const createStatusTransitionSequence = (callbacks, remainingTime) => {
  const timeouts = [];

  const steps = [
    {
      delay: remainingTime,
      callback: callbacks.onFadeOutStart,
    },
    {
      delay: ANIMATION_DURATIONS.BASE,
      callback: callbacks.onStatusHidden,
    },
    {
      delay: ANIMATION_DURATIONS.BASE,
      callback: callbacks.onContentDelayEnd,
    },
    {
      delay: ANIMATION_DURATIONS.BASE * 2,
      callback: callbacks.onTransitionComplete,
    },
  ];

  let currentStep = 0;

  const runStep = () => {
    if (currentStep >= steps.length) return;
    const { delay, callback } = steps[currentStep];
    const timeout = setTimeout(() => {
      callback?.();
      currentStep++;
      runStep();
    }, delay);
    timeouts.push(timeout);
  };

  runStep();

  // Retorna cleanup function
  return () => {
    for (const timeout of timeouts) {
      clearTimeout(timeout);
    }
  };
};

/**
 * Cria uma sequência encadeada de timeouts para transição entre rotas
 *
 * Gerencia a sequência completa de animações ao trocar de rota:
 * 1. Inicia fadeOut da rota antiga (imediato)
 * 2. Aguarda fadeOut completar (ANIMATION_DURATIONS.BASE)
 * 3. Troca para nova rota e inicia delay + fadeIn (ANIMATION_DURATIONS.BASE * 2)
 * 4. Marca transição como completa
 *
 * @param {Object} callbacks - Callbacks para cada etapa da transição
 * @param {Function} [callbacks.onExitStart] - Chamado imediatamente ao iniciar transição
 * @param {Function} [callbacks.onEnterStart] - Chamado após fadeOut, ao trocar para nova rota
 * @param {Function} [callbacks.onTransitionComplete] - Chamado ao fim de toda transição
 * @returns {Function} Cleanup function que cancela todos os timeouts pendentes
 *
 * @example
 * const cleanup = createRouteTransitionSequence({
 *   onExitStart: () => setTransitionStage("exitingOld"),
 *   onEnterStart: () => {
 *     setDisplayLocation(newLocation);
 *     setTransitionStage("enteringNew");
 *   },
 *   onTransitionComplete: () => setTransitionStage("fadeIn")
 * });
 * // Para cancelar: cleanup();
 */
export const createRouteTransitionSequence = (callbacks) => {
  const timeouts = [];

  // Etapa 1: Inicia fadeOut da rota antiga
  callbacks.onExitStart?.();

  // Etapa 2: Aguarda fadeOut e troca para nova rota
  const timeout1 = setTimeout(() => {
    callbacks.onEnterStart?.();

    // Etapa 3: Aguarda animação completa (delay + fadeIn)
    const timeout2 = setTimeout(() => {
      callbacks.onTransitionComplete?.();
    }, ANIMATION_DURATIONS.BASE * 2);

    timeouts.push(timeout2);
  }, ANIMATION_DURATIONS.BASE);

  timeouts.push(timeout1);

  // Retorna cleanup function
  return () => {
    for (const timeout of timeouts) {
      clearTimeout(timeout);
    }
  };
};
